import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Users,
  Plus,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Inbox,
  Clock,
  Activity,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'
import { useAgentsStore, type Agent, type QueueStats, type RouterStats } from '@/stores/agents'

const statusColors: Record<string, string> = {
  idle: 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30',
  busy: 'bg-blue-600/20 text-blue-400 border-blue-500/30',
  offline: 'bg-zinc-600/20 text-zinc-400 border-zinc-500/30',
  error: 'bg-red-600/20 text-red-400 border-red-500/30',
}

function AgentCard({ agent }: { agent: Agent }) {
  const heartbeatAgo = agent.lastHeartbeatAt
    ? Math.round((Date.now() - agent.lastHeartbeatAt) / 1000)
    : null

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{agent.name}</h3>
              <Badge variant="outline" className={statusColors[agent.status] || ''}>
                {agent.status}
              </Badge>
            </div>
            {agent.description && (
              <p className="text-sm text-muted-foreground">{agent.description}</p>
            )}
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {new Date(agent.registeredAt).toLocaleDateString()}
          </span>
        </div>

        {agent.capabilities.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {agent.capabilities.map((cap) => (
              <Badge key={cap} variant="secondary" className="text-xs">
                {cap}
              </Badge>
            ))}
          </div>
        )}

        {agent.channels.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {agent.channels.map((ch) => (
              <Badge key={ch} variant="outline" className="text-xs">
                #{ch}
              </Badge>
            ))}
          </div>
        )}

        {heartbeatAgo !== null && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            Heartbeat {heartbeatAgo}s ago
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function QueueStatsCard({ stats }: { stats: QueueStats }) {
  const items = [
    { label: 'Waiting', value: stats.waiting, color: 'text-amber-400' },
    { label: 'Active', value: stats.active, color: 'text-blue-400' },
    { label: 'Completed', value: stats.completed, color: 'text-emerald-400' },
    { label: 'Failed', value: stats.failed, color: 'text-red-400' },
    { label: 'Delayed', value: stats.delayed, color: 'text-purple-400' },
    { label: 'Paused', value: stats.paused, color: 'text-zinc-400' },
  ]

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Queue Stats
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3">
          {items.map((item) => (
            <div key={item.label} className="text-center space-y-0.5">
              <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
              <p className="text-xs text-muted-foreground">{item.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function RouterStatsCard({ stats }: { stats: RouterStats }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Router Stats
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center space-y-0.5">
            <p className="text-xl font-bold text-blue-400">{stats.messagesReceived}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <ArrowDownRight className="h-3 w-3" /> Received
            </p>
          </div>
          <div className="text-center space-y-0.5">
            <p className="text-xl font-bold text-emerald-400">{stats.messagesRouted}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <ArrowUpRight className="h-3 w-3" /> Routed
            </p>
          </div>
          <div className="text-center space-y-0.5">
            <p className="text-xl font-bold text-red-400">{stats.messagesFailed}</p>
            <p className="text-xs text-muted-foreground">Failed</p>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-2 pt-2 border-t border-border">
          {[
            { label: 'Clients', value: stats.clients },
            { label: 'Agents', value: stats.agents },
            { label: 'Rules', value: stats.rules },
            { label: 'Targets', value: stats.targets },
            { label: 'Queues', value: stats.queues },
          ].map((item) => (
            <div key={item.label} className="text-center">
              <p className="text-sm font-medium">{item.value}</p>
              <p className="text-xs text-muted-foreground">{item.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export default function AgentsPage() {
  const {
    agents,
    queueStats,
    routerStats,
    isLoading,
    error,
    fetchAgents,
    registerAgent,
    fetchQueueStats,
    fetchRouterStats,
  } = useAgentsStore()

  const [isRegistering, setIsRegistering] = useState(false)
  const [regName, setRegName] = useState('')
  const [regDescription, setRegDescription] = useState('')
  const [regCapabilities, setRegCapabilities] = useState('')
  const [regChannels, setRegChannels] = useState('')
  useEffect(() => {
    fetchAgents()
    fetchQueueStats()
    fetchRouterStats()
    const interval = setInterval(() => {
      fetchAgents()
      fetchQueueStats()
      fetchRouterStats()
    }, 10000)
    return () => clearInterval(interval)
  }, [fetchAgents, fetchQueueStats, fetchRouterStats])

  const handleRegister = async () => {
    if (!regName.trim()) return
    await registerAgent({
      name: regName.trim(),
      description: regDescription.trim() || undefined,
      capabilities: regCapabilities.split(',').map((s) => s.trim()).filter(Boolean),
      channels: regChannels.split(',').map((s) => s.trim()).filter(Boolean),
    })
    setRegName('')
    setRegDescription('')
    setRegCapabilities('')
    setRegChannels('')
    setIsRegistering(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Agents</h1>
          <p className="text-muted-foreground mt-1">
            Registered agents, queue stats, and message routing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isRegistering} onOpenChange={setIsRegistering}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Register Agent
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Register Agent</DialogTitle>
                <DialogDescription>
                  Register a new agent to the gateway message router.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="my-agent"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={regDescription}
                    onChange={(e) => setRegDescription(e.target.value)}
                    placeholder="What does this agent do?"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Capabilities (comma-separated)</Label>
                  <Input
                    value={regCapabilities}
                    onChange={(e) => setRegCapabilities(e.target.value)}
                    placeholder="coding, research, web-scraping"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Channels (comma-separated)</Label>
                  <Input
                    value={regChannels}
                    onChange={(e) => setRegChannels(e.target.value)}
                    placeholder="general, coding-tasks"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRegistering(false)}>
                  Cancel
                </Button>
                <Button onClick={handleRegister} disabled={!regName.trim()}>
                  Register
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchAgents()
              fetchQueueStats()
              fetchRouterStats()
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-500">{error}</p>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {queueStats && <QueueStatsCard stats={queueStats} />}
        {routerStats && <RouterStatsCard stats={routerStats} />}
      </div>

      {/* Registered Agents */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Registered Agents ({agents.length})
        </h2>
        {agents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Inbox className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No agents registered</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Register an agent to enable routing messages and task delegation
                through the gateway.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
